import { Global } from "../../utils/Global";
import { BasicWindow } from "./BasicWindow";
import { BasicButton } from "../BasicButton";
import { GameScene } from "../../scenes/GameScene";
import { ItemDropZone, DropZoneShape } from "../../utils/ItemDropZone";
import { SlotType, BaseItem } from "../../inventory/BaseItem";
import { NineSlicePresets } from "../NineSlicePresets";
import { Inventory } from "../Inventory";
import { Charms } from "../../inventory/Charms";
import { Items } from "../../inventory/Items";
import Client from "../..";

// Interface for inventory slot to avoid 'any' types
interface InventorySlotLike {
  item: BaseItem | null;
  ClearItem(): void;
}

export class ItemShop extends BasicWindow {
  private static readonly CHARM_SLOT_COUNT = 2;
  private static readonly ITEM_SLOT_COUNT = 4;
  public static readonly INITIAL_REROLL_PRICE = 1;

  private rerollButton!: RerollButton;
  private sellDropZone!: ItemDropZone<SlotType>;
  private sellZoneBackground!: Phaser.GameObjects.NineSlice;
  private sellZoneText!: Phaser.GameObjects.BitmapText;

  // Store content
  private storeTitle!: Phaser.GameObjects.BitmapText;
  private charmsLabel!: Phaser.GameObjects.BitmapText;
  private itemsLabel!: Phaser.GameObjects.BitmapText;
  private charmSlots!: Inventory;
  private itemSlots!: Inventory;
  private charmBuyButtons: BasicButton[] = [];
  private itemBuyButtons: BasicButton[] = [];

  private get gameScene(): GameScene {
    return this.scene as GameScene;
  }

  constructor(scene: Phaser.Scene) {
    super(
      scene,
      0, // x - will be positioned dynamically
      0, // y - will be positioned dynamically
      800, // width - will be resized dynamically
      600, // height - will be resized dynamically
      "Button1", // textureKey
      "", // frameKey
      800, // sliceWidth
      600, // sliceHeight
      7, // leftWidth
      7, // topHeight
      7, // rightWidth
      7, // bottomHeight
    );

    this.createContent();
    this.setDepth(5000); // Above basic UI but below inventory/charms
    this.setVisible(false); // Hidden by default
  }

  private createContent(): void {
    // Create store title
    this.storeTitle = new Phaser.GameObjects.BitmapText(
      this.scene,
      0,
      0,
      "fnt_paintBasic",
      "ITEM SHOP",
      Global.minFontSize * 2,
    ).setOrigin(0.5);

    // Create charms label (same size as sell text)
    this.charmsLabel = new Phaser.GameObjects.BitmapText(
      this.scene,
      0,
      0,
      "fnt_paintBasic",
      "Charms for sale:",
      Global.minFontSize * 1.2,
    ).setOrigin(0.5);

    // Create items label (same size as sell text)
    this.itemsLabel = new Phaser.GameObjects.BitmapText(
      this.scene,
      0,
      0,
      "fnt_paintBasic",
      "Items for sale:",
      Global.minFontSize * 1.2,
    ).setOrigin(0.5);

    // Create charm slots (non-draggable shop inventory)
    this.charmSlots = new ShopInventory(
      this.scene,
      ItemShop.CHARM_SLOT_COUNT,
      false,
    );
    this.createCharmBuyButtons();

    // Create item slots (non-draggable shop inventory)
    this.itemSlots = new ShopInventory(
      this.scene,
      ItemShop.ITEM_SLOT_COUNT,
      false,
    );
    this.createItemBuyButtons();

    // Initial stock will be generated when shop is first shown

    // Create reroll button
    this.rerollButton = new RerollButton(this.scene, () => {
      this.rerollStock();
    });

    // Create sell dropzone
    this.createSellDropZone();

    // Add all elements to content container
    this.addContent(this.storeTitle);
    this.addContent(this.charmsLabel);
    this.addContent(this.charmSlots);
    this.addContent(this.itemsLabel);
    this.addContent(this.itemSlots);
    this.addContent(this.rerollButton);
    this.addContent(this.sellZoneBackground);
    this.addContent(this.sellZoneText);
    this.addContent(this.sellDropZone);

    // Add buy buttons
    this.charmBuyButtons.forEach((button) => this.addContent(button));
    this.itemBuyButtons.forEach((button) => this.addContent(button));
  }

  private createCharmBuyButtons(): void {
    for (let i = 0; i < ItemShop.CHARM_SLOT_COUNT; i++) {
      const buyButton = new BuyButton(this.scene, () => this.buyCharm(i));
      buyButton.ResizeToFitText(); // Size button to fit text properly
      this.charmBuyButtons.push(buyButton);
    }
  }

  private createItemBuyButtons(): void {
    for (let i = 0; i < ItemShop.ITEM_SLOT_COUNT; i++) {
      const buyButton = new BuyButton(this.scene, () => this.buyItem(i));
      buyButton.ResizeToFitText(); // Size button to fit text properly
      this.itemBuyButtons.push(buyButton);
    }
  }

  private buyCharm(slotIndex: number): void {
    this.buyFromSlot<Charms>(
      this.charmSlots.inventorySlots[slotIndex],
      "charm",
      (key: Charms) => Client.state.charmInventory.AddItemToInventory(key),
      () => Client.state.charmInventory.freeSpaces,
    );
  }

  private buyItem(slotIndex: number): void {
    this.buyFromSlot<Items>(
      this.itemSlots.inventorySlots[slotIndex],
      "item",
      (key: Items) => Client.state.itemInventory.AddItemToInventory(key),
      () => Client.state.itemInventory.freeSpaces,
    );
  }

  private buyFromSlot<T extends Charms | Items>(
    slot: InventorySlotLike,
    itemType: string,
    addToInventory: (key: T) => void,
    getFreeSpaces: () => number,
  ): void {
    if (!slot.item) {
      console.log(`No ${itemType} in slot`);
      return;
    }

    const itemValue = slot.item.value || 0;
    if (Client.state.money < itemValue) {
      console.log(`Not enough money to buy ${itemType}`);
      return;
    }

    if (getFreeSpaces() <= 0) {
      console.log(`No free space in ${itemType} inventory`);
      return;
    }

    // Deduct money and add to player inventory
    Client.state.money -= itemValue;
    addToInventory((slot.item as BaseItem & { key: T }).key);

    // Clear the store slot
    slot.ClearItem();

    // Update button visibility after purchase
    this.updateBuyButtonVisibility();

    console.log(`Bought ${itemType} for $${itemValue}`);
  }

  private createSellDropZone(): void {
    const fontSize = Global.minFontSize;

    const defaultSize = 80;

    // Create brown background for sell zone with origin at center
    this.sellZoneBackground = NineSlicePresets.Default(this.scene)
      .setTint(0x732c40 )
      .setSize(defaultSize, defaultSize)
      .setDisplaySize(defaultSize, defaultSize)
      .setOrigin(0, 0); // Center origin to match dropzone

    // Create text above the dropzone
    this.sellZoneText = new Phaser.GameObjects.BitmapText(
      this.scene,
      0,
      -defaultSize / 2 - fontSize * 1.2,
      "fnt_paintBasic",
      "Put item here to sell:",
      fontSize * 1.2,
    ).setOrigin(0.5);

    // Create the actual dropzone with center origin
    this.sellDropZone = new ItemDropZone<SlotType>(
      this.scene,
      [SlotType.STORE],
      this.onSellZoneEnter.bind(this),
      this.onSellZoneLeave.bind(this),
      this.onSellZoneDrop.bind(this),
    );
    this.sellDropZone.SetShape(
      DropZoneShape.RECTANGLE,
      defaultSize,
      defaultSize,
    );
    this.sellDropZone.setOrigin(0.5); // Center origin to match background
  }

  private getInventorySlotSize(): number {
    // Check if itemInventory exists
    if (!this.gameScene.itemInventory?.inventorySlots) {
      return 80; // Fallback size
    }

    // Calculate the same slot size as used in Inventory.Resize()
    const { height, width } = this.gameScene.itemInventory;
    const numberOfSlots = this.gameScene.itemInventory.inventorySlots.length;

    // Use the same calculation as in Inventory.Resize() for horizontal inventory
    return Math.min(height, width / numberOfSlots);
  }

  private onSellZoneEnter(): void {
    // Visual feedback when item enters sell zone
    this.sellZoneBackground.setTint(0xac4c3f );
  }

  private onSellZoneLeave(): void {
    // Reset visual feedback when item leaves sell zone
    this.sellZoneBackground.setTint(0x732c40  );
  }

  private onSellZoneDrop(): boolean {
    // Reset visual feedback immediately when item is dropped
    this.sellZoneBackground.setTint(0x732c40  );
    // This will be called when an item is dropped on the sell zone
    // The actual selling logic is handled in the inventory slot's onDragDrop method
    // We just need to return true to indicate the drop was successful
    return true;
  }

  private generateCharmStock(): void {
    this.charmSlots.LoadState(new Array(ItemShop.CHARM_SLOT_COUNT).fill(null));
    this.generateStock(
      ItemShop.CHARM_SLOT_COUNT,
      "charm",
      () => Client.state.charmPool.GetRandomItem(),
      (item, i) => this.charmSlots.inventorySlots[i].FillItem(item),
    );
  }

  private generateItemStock(): void {
    this.itemSlots.LoadState(new Array(ItemShop.ITEM_SLOT_COUNT).fill(null));
    this.generateStock(
      ItemShop.ITEM_SLOT_COUNT,
      "item",
      () => Client.state.itemPool.GetRandomItem(),
      (item, i) => this.itemSlots.inventorySlots[i].FillItem(item),
    );
  }

  private generateStock(
    count: number,
    type: string,
    getRandomItem: () => BaseItem | null,
    fillSlot: (item: BaseItem, index: number) => void,
  ): void {
    for (let i = 0; i < count; i++) {
      const randomItem = getRandomItem();
      if (!randomItem) {
        console.warn(`No ${type}s available in ${type} pool`);
        continue;
      }

      // Create a copy with unique ID for the store
      const storeItem = {
        ...randomItem,
        uniqueId: `store_${type}_${i}_${Date.now()}`,
        effects: randomItem.effects?.map((e) => e.Clone()),
      };

      fillSlot(storeItem, i);
    }
  }

  /**
   * Generates fresh stock for the shop. Called when entering shop state and when rerolling.
   */
  public generateFreshStock(): void {
    this.generateCharmStock();
    this.generateItemStock();
    console.log("Shop stock generated!");
  }

  public resetShopForNewLevel(): void {
    // Reset reroll price when entering a new shop level
    this.rerollButton.resetPrice();
    // Generate fresh stock for the new shop
    this.generateFreshStock();
    // Update button visibility after generating new stock
    this.updateBuyButtonVisibility();
  }

  private updateBuyButtonVisibility(): void {
    // Update charm buy button visibility
    for (let i = 0; i < this.charmBuyButtons.length; i++) {
      const hasItem = this.charmSlots.inventorySlots[i].item !== null;
      this.charmBuyButtons[i].setVisible(hasItem);
    }

    // Update item buy button visibility
    for (let i = 0; i < this.itemBuyButtons.length; i++) {
      const hasItem = this.itemSlots.inventorySlots[i].item !== null;
      this.itemBuyButtons[i].setVisible(hasItem);
    }
  }

  private rerollStock(): void {
    const currentPrice = this.rerollButton.getCurrentPrice();

    // Check if player can afford the reroll
    if (Client.state.money < currentPrice) {
      console.log(
        `Cannot afford reroll! Need $${currentPrice}, have $${Client.state.money}`,
      );
      return;
    }

    // Deduct the cost
    Client.state.money -= currentPrice;

    // Generate new stock
    this.generateFreshStock();

    // Update button visibility after reroll
    this.updateBuyButtonVisibility();

    // Double the price for next reroll
    this.rerollButton.doublePrice();

    console.log(
      `Store stock rerolled for $${currentPrice}! Next reroll will cost $${this.rerollButton.getCurrentPrice()}`,
    );
  }

  public showShop(): void {
    this.show();
    this.gameScene.wheel?.disableAllInteractions();
  }

  public hideShop(): void {
    this.hide();
    // Always re-enable wheel interactions when shop window is closed
    // This allows players to use items on wheel sections during shop phase
    this.gameScene.wheel?.enableAllInteractions();
    this.gameScene.updateShopButtonState(false);
  }

  public positionElements(): void {
    const buttonPadding = Global.minFontSize;
    const shopWidth = this.background.width;
    const shopHeight = this.background.height;

    // Split shop into 3 vertical slices
    const sliceHeight = shopHeight / 3;
    const inventorySlotSize = this.getInventorySlotSize();

    // TOP SLICE: Store title and charms
    const topSliceY = -shopHeight / 2;

    // Position store title with padding from top border
    this.storeTitle.setPosition(
      0,
      topSliceY + buttonPadding + this.storeTitle.height / 2,
    );

    // Position charms label below title
    const charmsLabelY = topSliceY + buttonPadding * 2 + this.storeTitle.height;
    this.charmsLabel.setPosition(0, charmsLabelY);

    // Position charm slots below label (horizontal layout, center-aligned)
    const charmSlotsY =
      charmsLabelY + buttonPadding + this.charmsLabel.height / 2;
    const charmSlotWidth = inventorySlotSize * 2 + buttonPadding;
    this.charmSlots.setPosition(-charmSlotWidth / 2, charmSlotsY);
    // Force horizontal layout and proper sizing for 2 slots
    this.charmSlots.Resize(charmSlotWidth, inventorySlotSize);

    // Position charm buy buttons below charm slots
    const charmButtonY = charmSlotsY + inventorySlotSize + buttonPadding;
    for (let i = 0; i < this.charmBuyButtons.length; i++) {
      // Get the exact position of each charm slot to center the button underneath
      const slotX =
        this.charmSlots.inventorySlots[i].x +
        this.charmSlots.inventorySlots[i].width / 2 +
        this.charmSlots.x;
      this.charmBuyButtons[i].setPosition(slotX, charmButtonY);
    }

    // MIDDLE SLICE: Items for sale and reroll button
    const middleSliceY = topSliceY + sliceHeight + buttonPadding * 2;

    // Position items label at top of middle slice
    this.itemsLabel.setPosition(0, middleSliceY + buttonPadding);

    // Position item slots below label (centered horizontally)
    const itemSlotsY =
      middleSliceY + buttonPadding * 2 + this.itemsLabel.height / 2;
    const itemSlotWidth = inventorySlotSize * 4 + buttonPadding * 3;
    this.itemSlots.setPosition(-itemSlotWidth / 2, itemSlotsY);
    this.itemSlots.Resize(itemSlotWidth, inventorySlotSize);

    // Position item buy buttons below item slots
    const itemButtonY = itemSlotsY + inventorySlotSize + buttonPadding;
    for (let i = 0; i < this.itemBuyButtons.length; i++) {
      // Get the exact position of each item slot to center the button underneath
      const slotX =
        this.itemSlots.inventorySlots[i].x +
        this.itemSlots.inventorySlots[i].width / 2 +
        this.itemSlots.x;
      this.itemBuyButtons[i].setPosition(slotX, itemButtonY);
    }

    // Resize reroll button to match inventory slot size (square)
    this.rerollButton.Resize(inventorySlotSize, inventorySlotSize);
    // Override the automatic font sizing to use a more appropriate size
    this.rerollButton.SetFontSize(Global.minFontSize);
    // Reposition both texts to be centered together
    this.rerollButton.repositionTexts();

    const rerollButtonX =
      -shopWidth / 2.5 +
      this.rerollButton.width +
      buttonPadding -
      this.rerollButton.width;
    this.rerollButton.setPosition(rerollButtonX, this.itemsLabel.y);

    // BOTTOM SLICE: Sell zone
    const bottomSliceY = -shopHeight / 2 + 2 * sliceHeight;

    // Resize sell zone to match inventory slot size
    this.sellZoneBackground.setSize(inventorySlotSize, inventorySlotSize);
    this.sellZoneBackground.setDisplaySize(
      inventorySlotSize,
      inventorySlotSize,
    );
    this.sellDropZone.SetShape(
      DropZoneShape.RECTANGLE,
      inventorySlotSize,
      inventorySlotSize,
    );

    // Position the sell dropzone in the bottom slice, centered horizontally
    const sellZoneX = -inventorySlotSize / 2; // Center horizontally (origin 0,0 means top-left corner)
    const sellZoneY = bottomSliceY + (sliceHeight - inventorySlotSize) / 2; // Center in bottom slice

    this.sellZoneBackground.setPosition(sellZoneX, sellZoneY);
    this.sellZoneText.setPosition(
      sellZoneX + inventorySlotSize / 2,
      sellZoneY - Global.minFontSize * 1.2,
    ); // Center text above
    this.sellDropZone.setPosition(sellZoneX, sellZoneY); // Center dropzone
  }

  public override destroy(): void {
    // Clean up all components (Phaser handles null/undefined objects gracefully)
    this.rerollButton?.destroy();
    this.sellDropZone?.destroy();
    this.sellZoneBackground?.destroy();
    this.sellZoneText?.destroy();
    this.storeTitle?.destroy();
    this.charmsLabel?.destroy();
    this.itemsLabel?.destroy();
    this.charmSlots?.destroy();
    this.itemSlots?.destroy();

    // Clean up buy buttons
    this.charmBuyButtons.forEach((button) => button?.destroy());
    this.itemBuyButtons.forEach((button) => button?.destroy());

    super.destroy();
  }
}

// Custom button class for buying items
class BuyButton extends BasicButton {
  constructor(
    scene: Phaser.Scene,
    private onClickCallback?: () => void,
  ) {
    super(
      scene,
      "Buy",
      { textureKey: "Button1" },
      { textureKey: "Button2" },
      { textureKey: "Button1" },
      undefined,
    );
    this.background.setTint(0xac4c3f);
  }

  handlePointerDown(): void {}
  handlePointerOver(): void {}
  handlePointerOut(): void {}

  handlePointerUp(): void {
    this.onClickCallback?.();
  }
}

// Custom inventory class for shop items that disables dragging but keeps hover
class ShopInventory extends Inventory {
  constructor(scene: Phaser.Scene, numberOfSlots: number, vertical: boolean) {
    super(scene, numberOfSlots, vertical);

    // Override each slot to disable dragging after creation
    this.inventorySlots.forEach((slot) => {
      this.makeSlotNonDraggable(slot);
    });
  }

  private makeSlotNonDraggable(slot: any): void {
    // Override the FillItem method to prevent dragging setup and add price tag
    const originalFillItem = slot.FillItem.bind(slot);
    slot.FillItem = (item: BaseItem) => {
      // Call original FillItem first
      originalFillItem(item);

      // Then disable dragging if image exists
      if (slot.image) {
        slot.scene.input.setDraggable(slot.image, false);

        // Remove drag event listeners from this specific image but keep hover
        slot.image.off(Phaser.Input.Events.DRAG_START);
        slot.image.off(Phaser.Input.Events.DRAG_END);
        slot.image.off(Phaser.Input.Events.DROP);
        slot.image.off(Phaser.Input.Events.DRAG_ENTER);
        slot.image.off(Phaser.Input.Events.DRAG_LEAVE);

        // Don't remove the global DRAG listener - that affects all inventories
        // Instead, we rely on setDraggable(false) to prevent this image from being dragged

        // Add price tag to bottom right
        this.addPriceTag(slot, item);
      }
    };

    // Override ClearItem to remove price tag
    const originalClearItem = slot.ClearItem.bind(slot);
    slot.ClearItem = () => {
      this.removePriceTag(slot);
      originalClearItem();
    };
  }

  private addPriceTag(slot: any, item: BaseItem): void {
    // Remove existing price tag if any
    this.removePriceTag(slot);

    const slotSize = slot.background.width;
    const tagSize = slotSize * 0.4; // Price tag is 40% of slot size

    // Create price tag background using damagedSigns.png as 9-slice
    slot.priceTagBackground = new Phaser.GameObjects.NineSlice(
      slot.scene,
      0,
      0,
      "DamagedSigns",
      undefined,
      tagSize,
      tagSize * 0.6, // Make it more rectangular
      7, // leftWidth
      7, // topHeight
      7, // rightWidth
      7, // bottomHeight
    );

    // Apply yellowish cardboard tint
    slot.priceTagBackground.setTint(0xfff2c4 );

    // Position at bottom right of slot
    const tagX = slotSize - tagSize / 2;
    const tagY = slotSize - tagSize * 0.4;
    slot.priceTagBackground.setPosition(tagX, tagY);

    // Create price text
    slot.priceTagText = new Phaser.GameObjects.BitmapText(
      slot.scene,
      tagX,
      tagY,
      "fnt_paintBasic",
      `$${item.value || 0}`,
      Global.minFontSize, // Smaller font for price tag
    );
    slot.priceTagText.setOrigin(0.5);
    slot.priceTagText.setTint(0xac4c3f);

    // Add to slot container
    slot.add(slot.priceTagBackground);
    slot.add(slot.priceTagText);
  }

  private removePriceTag(slot: any): void {
    if (slot.priceTagBackground) {
      slot.priceTagBackground.destroy();
      slot.priceTagBackground = null;
    }
    if (slot.priceTagText) {
      slot.priceTagText.destroy();
      slot.priceTagText = null;
    }
  }
}

// Custom reroll button class for the item shop
class RerollButton extends BasicButton {
  private priceText!: Phaser.GameObjects.BitmapText;
  private rerollPrice: number = ItemShop.INITIAL_REROLL_PRICE;

  constructor(
    scene: Phaser.Scene,
    private onClickCallback?: () => void,
  ) {
    super(
      scene,
      "Reroll",
      { textureKey: "Button1" },
      { textureKey: "Button2" },
      { textureKey: "Button1" },
      undefined,
    );
    this.background.setTint(0xac4c3f);
    this.createPriceText();
  }

  private createPriceText(): void {
    this.priceText = new Phaser.GameObjects.BitmapText(
      this.scene,
      0,
      this.height / 2 + Global.minFontSize,
      "fnt_paintBasic",
      `$${this.rerollPrice}`,
      Global.minFontSize,
    );
    this.priceText.setOrigin(0.5);
    this.priceText.setTint(0xfff57a );
    this.add(this.priceText);
  }

  public setPrice(price: number): void {
    this.rerollPrice = price;
    this.priceText.setText(`$${price}`);
  }

  public doublePrice(): void {
    this.rerollPrice *= 2;
    this.priceText.setText(`$${this.rerollPrice}`);
  }

  public resetPrice(): void {
    this.rerollPrice = ItemShop.INITIAL_REROLL_PRICE;
    this.priceText.setText(`$${this.rerollPrice}`);
  }

  public getCurrentPrice(): number {
    return this.rerollPrice;
  }

  public repositionTexts(): void {
    // Calculate the total height of both texts
    const mainTextHeight = this.text.displayHeight;
    const priceTextHeight = this.priceText.displayHeight;
    const textSpacing = Global.minFontSize * 0.2; // Small spacing between texts
    const totalTextHeight = mainTextHeight + priceTextHeight + textSpacing;

    // Center both texts as a group
    const startY = -totalTextHeight / 2;

    // Position main text ("Reroll") at the top of the group
    this.text.setPosition(0, startY + mainTextHeight / 2);

    // Position price text ("$10") below the main text
    this.priceText.setPosition(
      0,
      startY + mainTextHeight + textSpacing + priceTextHeight / 2,
    );
  }

  handlePointerDown(): void {}
  handlePointerOver(): void {}
  handlePointerOut(): void {}

  handlePointerUp(): void {
    this.onClickCallback?.();
  }

  public override destroy(): void {
    this.priceText?.destroy();
    super.destroy();
  }
}
